/**
 * Template Models for Report Editor
 */

import { ConditionCategory, ConditionValueType } from './condition-registry.model';

// ============================================
// AUDIT FIELDS (Common to all entities)
// ============================================

/**
 * Common audit fields for all database entities.
 * Supports soft delete via isDeleted flag.
 */
export interface AuditFields {
  createdAt?: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: number;
}

// ============================================
// TEMPLATE BLOCK DEFINITIONS
// ============================================

export enum BlockType {
  Text = 1,
  Table = 2,
  Chart = 3,
  Header = 4,
  Image = 5,
  Toc = 6,
  Cover = 7,
}

export enum PlaceholderType {
  String = 1,
  Number = 2,
  Array = 3,
  Object = 4,
  Html = 5,
  Image = 6,
}

export enum PlaceholderSource {
  Data = 1,
  Auto = 2,
  Template = 3,
  User = 4,
}

export enum FormatType {
  Scientific = 1,
  Percentage = 2,
  Currency = 3,
  Date = 4,
  Number = 5,
}

export enum AutoType {
  SectionCounter = 1,
  TableCounter = 2,
  FigureCounter = 3,
  PageNumber = 4,
  SubSectionCounter = 5,
  ChildSectionCounter = 6,
}

/**
 * Condition for block-level visibility (AND logic — all must pass)
 */
export interface VisibilityCondition {
  expression: string; // e.g., 'showHideSection.isIntroduction'
  description?: string; // Human-readable description
  negate?: boolean; // If true, invert the result
}

/**
 * Documents an inline condition used inside htmlTemplate ([#if], [#switch], [#each])
 */
export interface InlineCondition {
  key: string; // Unique key, e.g., 'custom_intro'
  expression: string; // e.g., 'goalAndScope.introductionToISOReport'
  description?: string; // Human-readable description
  dependsOn: string[]; // Data paths this condition depends on
}

/**
 * Defines a placeholder within a template
 */
export interface PlaceholderDefinition {
  key: string;
  type: PlaceholderType;
  source: PlaceholderSource;
  path?: string; // Path in data (e.g., 'projectInfo.name')
  default?: any; // Default value if not found
  required?: boolean;
  editable?: boolean; // Can user edit this field?
  format?: FormatType; // Formatting to apply
  autoType?: AutoType; // For auto-generated values
  label?: string; // Display label for editing UI
}

/**
 * Represents a reusable template block
 */
export interface TemplateBlock {
  id: number;
  /** URL-safe AES-encrypted id produced by the backend (CommonHelper.EncryptId). */
  encryptedId?: string;
  blockCode: string;
  blockName: string;
  blockType: BlockType;
  category: string;
  htmlTemplate: string;
  /** @deprecated Parsed at runtime from htmlTemplate. Use PlaceholderMaster registry. */
  placeholderSchema?: PlaceholderDefinition[];
  defaultStyles?: string;
  isActive: boolean;
  isEditable?: boolean;
  sortOrder: number;
  description?: string;
  previewImage?: string;
  /** @deprecated Use BlockConditionMapping + ConditionMaster instead. */
  visibilityConditions?: VisibilityCondition[];
  inlineConditions?: InlineCondition[]; // Documents inline [#if]/[#switch] in htmlTemplate
  tag?: string;
  organizationId?: number | null;
  sourceBlockId?: number | null;
  isGlobal?: boolean;
  createdAt?: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: number;
}

// ============================================
// REPORT TEMPLATE DEFINITIONS
// ============================================

export enum PageSize {
  A4 = 1,
  Letter = 2,
  Legal = 3,
  Slide = 4,
}

export enum Orientation {
  Portrait = 1,
  Landscape = 2,
}

/**
 * Represents a report template configuration
 */
export interface ReportTemplate {
  id: number;
  /** URL-safe AES-encrypted id produced by the backend (CommonHelper.EncryptId). */
  encryptedId?: string;
  templateCode: string;
  templateName: string;
  description?: string;
  pageSize: PageSize;
  orientation: Orientation;
  globalStyles?: string;
  headerHtml?: string;
  footerHtml?: string;
  isActive: boolean;
  previewImage?: string;
  /** Nested block details returned by GET /templates/:id */
  templateBlocks?: TemplateBlockDetail[];
  organizationId?: number | null;
  isGlobal?: boolean;
  createdAt?: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: number;
}

/**
 * Nested block detail returned inside ReportTemplate.templateBlocks[]
 */
export interface TemplateBlockDetail {
  id: number;
  reportTemplateId: number;
  templateBlockId: number;
  sortOrder: number;
  pageBreakBefore?: boolean;
  pageBreakAfter?: boolean;
  detail: TemplateBlock;
}

/**
 * Maps blocks to templates with ordering
 */
export interface TemplateBlockMapping {
  id: number;
  reportTemplateId: number;
  templateBlockId: number;
  sectionNumber: string;
  sectionTitle: string;
  isRequired: boolean;
  isDefaultSelected: boolean;
  sortOrder: number;
  pageBreakBefore?: boolean;
  pageBreakAfter?: boolean;
  /** @deprecated Conditions managed through BlockConditionMapping. */
  overrideConditions?: VisibilityCondition[];
  createdAt?: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: number;
}

/**
 * Block info with mapping details (for display)
 */
export interface MappedBlock {
  blockId: number;
  blockCode: string;
  blockName: string;
  blockType: BlockType;
  category: string;
  sectionNumber: string;
  sectionTitle: string;
  isRequired: boolean;
  isDefaultSelected: boolean;
  sortOrder: number;
  pageBreakBefore?: boolean;
  htmlTemplate: string;
  /** @deprecated Parsed at runtime from htmlTemplate. */
  placeholderSchema?: PlaceholderDefinition[];
}

/**
 * Combined template with its blocks
 */
export interface ReportTemplateWithBlocks extends ReportTemplate {
  blocks: MappedBlock[];
}

// ============================================
// REPORT STATE
// ============================================

/**
 * User edits stored separately from original data
 */
export interface BlockEdit {
  blockId: number;
  editedContent: { [placeholderKey: string]: any };
  isModified: boolean;
  editedAt: string;
}

export interface UserEdits {
  [blockId: string]: BlockEdit;
}

/**
 * Auto-incrementing counters for sections, tables, figures
 */
export interface ReportCounters {
  section: number;
  table: number;
  figure: number;
}

/**
 * Processed block ready for rendering
 */
export interface ProcessedBlock {
  blockId: number;
  blockCode: string;
  blockType: string;
  renderedHtml: string;
  styles?: string;
  isModified: boolean;
  sectionNumber?: string;
  pageBreakBefore?: boolean;
}

/**
 * Current state of report being generated/edited
 */
export interface ReportState {
  templateId: number;
  templateCode: string;
  projectId: number;
  selectedBlockIds: number[];
  blockOrder: number[];
  userEdits: UserEdits;
  counters: ReportCounters;
  isLoading: boolean;
  isModified: boolean;
  lastSaved?: string;
}

// ============================================
// EXPORT OPTIONS
// ============================================

export enum ExportFormat {
  Pdf = 1,
  Docx = 2,
  Html = 3,
}

export interface ExportOptions {
  format: ExportFormat;
  filename?: string;
  includeStyles?: boolean;
  pageSize?: PageSize;
  orientation?: Orientation;
  includeTableOfContents?: boolean;
  includePageNumbers?: boolean;
  headerText?: string;
  footerText?: string;
}

// ============================================
// DTOs
// ============================================

export interface CreateBlockDto {
  blockCode: string;
  blockName: string;
  blockType: BlockType;
  category: string;
  htmlTemplate: string;
  /** @deprecated Placeholders parsed at runtime from htmlTemplate. */
  placeholderSchema?: PlaceholderDefinition[];
  defaultStyles?: string;
  description?: string;
  conditionMappings?: { conditionId: number; negate: boolean }[];
}

export interface UpdateBlockDto extends Partial<CreateBlockDto> {
  isActive?: boolean;
  sortOrder?: number;
}

export interface CreateTemplateDto {
  templateCode: string;
  templateName: string;
  description?: string;
  pageSize: PageSize;
  orientation?: Orientation;
  globalStyles?: string;
  blockIds: number[];
  blockMappings?: {
    templateBlockId: number;
    sortOrder: number;
    pageBreakBefore: boolean;
    pageBreakAfter: boolean;
  }[];
}

export interface UpdateTemplateDto extends Partial<CreateTemplateDto> {
  isActive?: boolean;
}

// ============================================
// PLACEHOLDER MASTER (Table 4)
// ============================================

/**
 * Central registry of all placeholders used across template blocks.
 * Placeholders are auto-discovered by parsing block HTML.
 */
export interface PlaceholderMaster {
  id: number;
  key: string;
  type: PlaceholderType;
  source: PlaceholderSource;
  path?: string;
  defaultValue?: any;
  required?: boolean;
  editable?: boolean;
  format?: FormatType;
  autoType?: AutoType;
  label?: string;
  createdAt?: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: number;
}

export interface CreatePlaceholderDto {
  key: string;
  type: PlaceholderType;
  source: PlaceholderSource;
  path?: string;
  defaultValue?: any;
  required?: boolean;
  editable?: boolean;
  format?: FormatType;
  autoType?: AutoType;
  label?: string;
}

export interface UpdatePlaceholderDto extends Partial<CreatePlaceholderDto> {}

// ============================================
// CONDITION MASTER (Table 5)
// ============================================

/**
 * Persistent condition definition stored in database.
 * Replaces the in-memory ConditionRegistryService registry.
 */
export interface ConditionMaster {
  id: number;
  category: ConditionCategory;
  expression: string;
  description?: string;
  valueType: ConditionValueType;
  comparisonOptions?: {
    operator: '==' | '!=' | '>' | '<' | '>=' | '<=';
    values: { label: string; value: any }[];
  };
  createdAt?: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: number;
}

export interface CreateConditionDto {
  category: ConditionCategory;
  expression: string;
  description?: string;
  valueType: ConditionValueType;
  comparisonOptions?: ConditionMaster['comparisonOptions'];
}

export interface UpdateConditionDto extends Partial<CreateConditionDto> {}

// ============================================
// BLOCK CONDITION MAPPING (Table 6)
// ============================================

/**
 * Links a TemplateBlock to a ConditionMaster entry.
 * Replaces the embedded visibilityConditions[] on TemplateBlock.
 * Multiple mappings per block = AND logic (all must pass).
 */
export interface BlockConditionMapping {
  id: number;
  templateBlockId: number;
  conditionId: number;
  negate: boolean;
  createdAt?: string;
  createdBy?: number;
  updatedAt?: string;
  updatedBy?: number;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: number;
}

export interface ReportPreviewData {
  template: ReportTemplate;
  mappings: TemplateBlockMapping[];
  blocks: TemplateBlock[];
  blockConditions: { [blockId: number]: VisibilityCondition[] };
}
